const BASE = '/api'

function getToken() { return localStorage.getItem('adops_token') }
export function setToken(t) { localStorage.setItem('adops_token', t) }
export function clearToken() { localStorage.removeItem('adops_token') }

// Report a failed request to the backend error log (never throws)
async function reportFailure(method, path, status, message) {
  try {
    if (path === '/report-error') return  // never loop
    const token = getToken()
    if (!token) return
    await fetch(`${BASE}/report-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        message: `API ${method} ${path} failed: ${message}`,
        stack: '',
        context: { method, path, status, page: window.location.hash || window.location.pathname },
        url: window.location.href
      })
    })
  } catch {}
}

async function req(method, path, body) {
  const token = getToken()
  let res
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: body ? JSON.stringify(body) : undefined
    })
  } catch (netErr) {
    // Background pollers (dashboard badges) fail with status 0 whenever a tab is left
    // open and the network sleeps — harmless and unactionable, so don't alert on those.
    // A genuine dashboard bug returns a 500, which still reports below.
    const silentPaths = ['/dashboard']
    if (!silentPaths.some(sp => path.startsWith(sp))) {
      reportFailure(method, path, 0, 'Network error: ' + netErr.message)
    }
    throw new Error('Network error — check your connection')
  }
  let data
  try { data = await res.json() }
  catch {
    if (!res.ok) {
      reportFailure(method, path, res.status, `Non-JSON response (HTTP ${res.status})`)
      throw new Error(`Request failed (HTTP ${res.status})`)
    }
    return {}
  }
  if (!res.ok) {
    const msg = data.error || 'Request failed'
    // 401/403 are normal auth events, not bugs — don't alert on those
    if (res.status !== 401 && res.status !== 403) reportFailure(method, path, res.status, typeof msg === 'string' ? msg : JSON.stringify(msg))
    throw new Error(msg)
  }
  return data
}

export const api = {
  // Auth
  login:          (email, password) => req('POST', '/auth/login', { email, password }),
  me:             ()                => req('GET',  '/auth/me'),
  changePassword: (body)            => req('PATCH', '/auth/password', body),

  // Users
  getUsers:   ()       => req('GET',    '/auth/users'),
  getAssignableUsers: () => req('GET', '/auth/assignable-users'),
  createUser: (body)   => req('POST',   '/auth/users', body),
  updateUserMarkets: (id, allowed_markets) => req('PATCH', `/auth/users/${id}/markets`, { allowed_markets }),
  updateUser: (id, b)  => req('PATCH',  `/auth/users/${id}`, b),
  deleteUser: (id)     => req('DELETE', `/auth/users/${id}`),
  reinvite:   (id)     => req('POST',   `/auth/users/${id}/reinvite`),
  validateInvite: (token)        => req('GET',  `/auth/invite/${token}`),
  acceptInvite:   (token, password) => req('POST', `/auth/invite/${token}`, { password }),

  getResources:    ()       => req('GET',    '/resources'),
  createResource:  (body)   => req('POST',   '/resources', body),
  updateResource:  (id, b)  => req('PATCH',  `/resources/${id}`, b),
  deleteResource:  (id)     => req('DELETE', `/resources/${id}`),

  // Products
  getProducts:   ()      => req('GET',    '/products'),
  getProductHtml: (id, which) => req('GET', `/products/${id}/html?which=${which}`),
  setProductHtml: (id, field, html) => req('PATCH', `/products/${id}`, { [field]: html }),
  createProduct: (body)  => req('POST',   '/products', body),
  updateProduct: (id, b) => req('PATCH',  `/products/${id}`, b),
  productDownstream: (pid)      => req('GET', `/products/${pid}/downstream`),
  productCleanup: (pid, body)   => req('POST', `/products/${pid}/cleanup`, body),
  deleteProduct: (id)    => req('DELETE', `/products/${id}`),

  // Sections — funnel, creatives, adcopy, scheduling, winning
  getSection:    (s, market) => req('GET',   `/${s}${market ? `/market/${market}` : ''}`),
  updateSection: (s, id, b)  => req('PATCH', `/${s}/${id}`, b),
  createSection: (s, body)   => req('POST',  `/${s}`, body),
  deleteSection: (s, id)     => req('DELETE',`/${s}/${id}`),
  // FB Infrastructure
  fbList:   (level, parent) => req('GET', `/fbinfra/${level}${parent?`?parent=${parent}`:''}`),
  fbCreate: (level, body)   => req('POST', `/fbinfra/${level}`, body),
  fbUpdate: (level, id, b)  => req('PATCH', `/fbinfra/${level}/${id}`, b),
  fbDelete: (level, id)     => req('DELETE', `/fbinfra/${level}/${id}`),
  fbOverview: ()            => req('GET', '/fbinfra/overview'),
  shopStores: ()            => req('GET', '/shopify/stores'),
  shopCreateStore: (b)      => req('POST', '/shopify/stores', b),
  shopUpdateStore: (id,b)   => req('PATCH', `/shopify/stores/${id}`, b),
  shopDeleteStore: (id)     => req('DELETE', `/shopify/stores/${id}`),
  shopTestStore: (id)       => req('POST', `/shopify/stores/${id}/test`, {}),
  shopSyncAll: ()           => req('POST', '/shopify/sync', {}),
  shopSyncStore: (id)       => req('POST', `/shopify/stores/${id}/sync`, {}),
  shopOrders: (archived)    => req('GET', `/shopify/orders${archived?'?archived=1':''}`),
  fbTree: ()                => req('GET', '/fbinfra/tree'),

  // Settings
  getSettings:    ()        => req('GET',   '/settings'),
  updateSetting:  (key, value) => req('PATCH', `/settings/${key}`, { value }),

  // Content generation
  generateContent: (body)        => req('POST', '/content/generate', body),
  listContent:     (product_id)  => req('GET',  `/content/list/${product_id}`),
  importFunnel:    (body)        => req('POST', '/content/import-funnel', body),
  importAdCopy:    (body)        => req('POST', '/content/import-adcopy', body),
  importAll:       (body)        => req('POST', '/content/import-all', body),
  // Payroll
  payEmployees:  ()             => req('GET', '/payroll/employees'),
  payTimeList:   (uid)          => req('GET', `/payroll/time-entries/${uid}`),
  payTimeAdd:    (body)         => req('POST', '/payroll/time-entries', body),
  payTimeUpdate: (id, body)     => req('PATCH', `/payroll/time-entries/${id}`, body),
  payTimeDelete: (id)           => req('DELETE', `/payroll/time-entries/${id}`),
  payRuns:       ()             => req('GET', '/payroll/runs'),
  payRunGet:     (id)           => req('GET', `/payroll/runs/${id}`),
  payRunPreview: (body)         => req('POST', '/payroll/runs/preview', body),
  payRunCreate:  (body)         => req('POST', '/payroll/runs', body),
  payRunUpdate:  (id, body)     => req('PATCH', `/payroll/runs/${id}`, body),
  payRunDelete:  (id)           => req('DELETE', `/payroll/runs/${id}`),
  getChangelog:  ()             => req('GET', '/company/changelog'),
  getActivityLog: (q='')        => req('GET', `/company/activity-log${q}`),
  getDashboard:  ()             => req('GET', '/dashboard'),
  getRates:      ()             => req('GET', '/pricing/rates'),
  pricingStrategy: (body)       => req('POST', '/content/pricing-strategy', body),
  pricingAgentAnalyze: (body)   => req('POST', '/pricing-agent/analyze', body),
  pricingAgentStatus: (jobId)   => req('GET', `/pricing-agent/status/${jobId}`),
  pricingAgentApply:   (body)   => req('POST', '/pricing-agent/apply', body),
}
